import { c as _c } from "react/compiler-runtime";
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import type { TouchDesignSerie } from '@/components/charts/interfaces';
import { useGenerateSerie } from '@/components/charts/hooks/use-generate-serie';
import { useCallback, useMemo } from 'react';
import { TouchDesignSerieVariant } from '@/components/charts/KeyboardChart';
import { FrontWeightDesignMode, type FrontWeightDesignTarget } from '@/lib/piano/touch-design/design/front-weight-design';
import { useInterpolatedSerieCubicSpline } from '@/hooks/series/use-interpolated-serie-cubic-spline';
import { generateCustomFrontWeightKeypoints } from '@/lib/piano/touch-design/generate-custom-front-weight-keypoints';
import { useGenerateDesignedKeyboardFromSeries } from '@/hooks/keyboard/use-generate-designed-keyboard-from-series';
import { frontWeightCalculatorRequirementsSymbol, type FrontWeightCalculatorRequirements } from '@/lib/piano/touch-design/front-weight-calculator/front-weight-calculator.requirements';
import { useInjection } from 'inversify-react';
import { useTranslation } from '@/hooks/use-translation';
export const useFrontWeightTargetSerie = keyboard => {
  const $ = _c(15);
  const {
    t
  } = useTranslation();
  const {
    generateSerie
  } = useGenerateSerie(keyboard);
  const {
    generateCubicInterpolatedKeyboardSerie,
    interpolateStandardKeypointsToKeyboardSize
  } = useInterpolatedSerieCubicSpline(keyboard);
  const {
    generateDesignedKeyboardFromSeries
  } = useGenerateDesignedKeyboardFromSeries(keyboard);
  const frontWeightCalculator = useInjection(frontWeightCalculatorRequirementsSymbol);
  let t0;
  if ($[0] !== t) {
    t0 = t("Target");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const name = t0;
  const variant = TouchDesignSerieVariant.Target;
  let t1;
  if ($[2] !== generateSerie || $[3] !== name) {
    t1 = generateSerie(_temp, name, variant);
    $[2] = generateSerie;
    $[3] = name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const nullTargetSerie = t1;
  let t2;
  if ($[5] !== frontWeightCalculator || $[6] !== generateCubicInterpolatedKeyboardSerie || $[7] !== generateDesignedKeyboardFromSeries || $[8] !== generateSerie || $[9] !== interpolateStandardKeypointsToKeyboardSize || $[10] !== name || $[11] !== nullTargetSerie) {
    t2 = (mode, target, strikeWeightTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie) => {
      if (mode === FrontWeightDesignMode.AsMeasured) {
        return generateSerie(_temp2, name, variant);
      }
      if (mode === FrontWeightDesignMode.Computed && target !== null) {
        const keyboardWithTargetedFrontWeight = generateDesignedKeyboardFromSeries({
          frontWeightTargetSerie: nullTargetSerie,
          strikeWeightRatioTargetSerie,
          strikeWeightTargetSerie,
          supportSpringBalanceWeightTargetSerie
        }).map(key_0 => frontWeightCalculator.calculateFrontWeight(key_0, Number(target)));
        return generateSerie(key_1 => keyboardWithTargetedFrontWeight.getKeyByNumber(key_1.number)?.payload.frontWeight ?? null, name, variant);
      }
      const targetNumber = Number(target);
      if (mode === FrontWeightDesignMode.StandardCurves && target !== null && !isNaN(targetNumber)) {
        const frontWeightCurve = generateCubicInterpolatedKeyboardSerie(interpolateStandardKeypointsToKeyboardSize(generateCustomFrontWeightKeypoints(targetNumber)));
        return generateSerie(key_2 => frontWeightCurve[key_2.number - 1] ?? null, name, variant);
      }
      return nullTargetSerie;
    };
    $[5] = frontWeightCalculator;
    $[6] = generateCubicInterpolatedKeyboardSerie;
    $[7] = generateDesignedKeyboardFromSeries;
    $[8] = generateSerie;
    $[9] = interpolateStandardKeypointsToKeyboardSize;
    $[10] = name;
    $[11] = nullTargetSerie;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const generateFrontWeightTargetSerie = t2;
  let t3;
  if ($[13] !== generateFrontWeightTargetSerie) {
    t3 = {
      generateFrontWeightTargetSerie
    };
    $[13] = generateFrontWeightTargetSerie;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
function _temp() {
  return null;
}
function _temp2(key) {
  return key.payload.frontWeight;
}